/**
 * Knows whether the running bundle is the one the server expects.
 *
 * A browser holds the HTML and JS it already has, and nothing on the server can
 * make an open tab reload. But the tab keeps calling the API, so every response
 * carries the build that should be running (X-Frontend-Version) and this
 * compares it to the build actually running. No polling, no extra request, and
 * nothing in the path that a browser or proxy could serve from cache.
 */

type Listener = (stale: boolean) => void

/** Dismissals are remembered per target build, so a new release asks again. */
const DISMISSED_KEY = 'portal:update-dismissed'

class VersionStore {
  /** The build the server says should be running. Null until a response says. */
  private expected: string | null = null

  private dismissed: string | null = null

  private listeners = new Set<Listener>()

  constructor() {
    try {
      this.dismissed = sessionStorage.getItem(DISMISSED_KEY)
    } catch {
      // Private browsing can refuse sessionStorage. Losing the dismissal only
      // means the prompt reappears, which is better than failing to load.
    }
  }

  /**
   * The build actually running, substituted at compile time by vite.config.js.
   *
   * Guarded rather than referenced bare. A bare reference is a hard
   * ReferenceError anywhere the define is missing, which takes the whole app
   * down over a prompt that is only ever a convenience: a dev server started
   * before the config changed, a test runner, any tooling that does not apply
   * it. Falling back to 'dev' just means never prompting, which is the right
   * failure for this.
   */
  get running(): string {
    return typeof __APP_VERSION__ === 'undefined' ? 'dev' : __APP_VERSION__
  }

  isStale(): boolean {
    // 'dev' is a local build with no release behind it, and an absent header
    // means the server has never been told a version. Neither can be stale.
    if (this.running === 'dev' || !this.expected) return false
    if (this.expected === this.running) return false

    return this.expected !== this.dismissed
  }

  /** Record the build named by a response. */
  observed(version: string | null): void {
    if (!version || version === this.expected) return

    this.expected = version
    this.emit()
  }

  dismiss(): void {
    this.dismissed = this.expected
    try {
      if (this.dismissed) sessionStorage.setItem(DISMISSED_KEY, this.dismissed)
    } catch {
      /* see constructor */
    }
    this.emit()
  }

  /** Named for what the customer is offered ("Refresh"), not the DOM call. */
  refresh(): void {
    // A full navigation, not react-router: index.html is served no-store, so
    // this is what fetches the new shell and its hashed assets.
    window.location.reload()
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit(): void {
    const stale = this.isStale()
    this.listeners.forEach(listener => listener(stale))
  }
}

export const versionStore = new VersionStore()
